import { useCallback } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

import { getCookieValue } from "@utils/browser-features";
import { logError, logWarning } from "@utils/rollbar";

import { ResultDTO } from "@organisms/result/domain/result";
import { createResult } from "@organisms/result/repositories/result-repository";

async function waitForRecaptchaReady(
  executeFn: (() => Promise<string>) | undefined,
  maxWaitMs: number
): Promise<void> {
  const intervalMs = 100;
  const start = Date.now();

  return new Promise((resolve, reject) => {
    const timer = setInterval(() => {
      if (executeFn) {
        clearInterval(timer);
        resolve();
      } else if (Date.now() - start >= maxWaitMs) {
        clearInterval(timer);
        reject(new Error("reCAPTCHA not ready after timeout"));
      }
    }, intervalMs);
  });
}

export function useCreateResultWithoutRecaptcha(): (
  values: Record<string, unknown>
) => Promise<ResultDTO> {
  return useCallback(async (values: Record<string, unknown>): Promise<ResultDTO> => {
    return createResult(JSON.stringify(values));
  }, []);
}

export function useRecaptchaToken(): () => Promise<string | undefined> {
  const { executeRecaptcha } = useGoogleReCaptcha();

  return useCallback(async (): Promise<string | undefined> => {
    const cachedToken = getCookieValue("SKIP_RECAPTCHA_TOKEN");

    if (cachedToken) {
      return cachedToken;
    }

    const maxWaitMs = 60 * 1000;

    try {
      await waitForRecaptchaReady(executeRecaptcha, maxWaitMs);
    } catch (err) {
      logWarning("reCAPTCHA executor did not become available in time", err as Error);

      return;
    }

    if (!executeRecaptcha) {
      logWarning("executeRecaptcha is not defined");

      return;
    }

    try {
      return await executeRecaptcha("create_result");
    } catch (error) {
      logError("Error on executeRecaptcha", error as Error);

      return;
    }
  }, [executeRecaptcha]);
}
